import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { FormRow } from '@/components/Form'
import { Button } from '@/components/Button'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import styles from './RateProDialog.module.css'
import { ThumbsRating } from '@/features/jobs/details/ratings/components/ThumbsRating'
import { StarRating } from '@/features/jobs/details/ratings/components/StarRating'
import { match } from '@/utils/pattern-match-util'

export type RatingInputVariant = 'Thumbs' | 'Stars'

export interface RateProDialogProps {
  showing: boolean
  proAvatar?: AvatarSubject
  proGivenName?: string
  ratingMethod: RatingInputVariant
  rating: number
  thumbsRating?: boolean
  feedbackField: ReactNode
  isRateProPending: boolean
  validRating: boolean
  onClose: () => unknown
  onRatingChange: (value: number) => unknown
  onThumbsRatingChange: (value: boolean) => unknown
  onRatePro: () => unknown
}

export function RateProDialog({
  showing,
  proAvatar,
  proGivenName,
  ratingMethod,
  rating,
  thumbsRating,
  feedbackField,
  isRateProPending,
  validRating,
  onClose,
  onRatingChange,
  onThumbsRatingChange,
  onRatePro,
}: RateProDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isRateProPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <div className={styles.avatar}>
            <Avatar
              subject={proAvatar}
              size={112}
              backgroundColor="var(--color-taxfyle-blue)"
            ></Avatar>
          </div>

          <ModalSheetContentTitle>
            How was {proGivenName}'s service?
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Our Pros appreciate your reviews.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <FormRow>
            {match(ratingMethod, {
              Stars: () => (
                <StarRating
                  iconName="star2"
                  iconSize={56}
                  value={rating}
                  onChange={onRatingChange}
                  testId="star-value"
                ></StarRating>
              ),
              Thumbs: () => (
                <ThumbsRating
                  iconSize={32}
                  value={thumbsRating}
                  onChange={onThumbsRatingChange}
                />
              ),
            })}
          </FormRow>
          <FormRow>{feedbackField}</FormRow>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            onPress={onClose}
            isDisabled={isRateProPending}
            testId="rate-pro-close-button"
          />
          <Button
            label="Submit Feedback"
            variant="primary"
            onPress={onRatePro}
            testId="rate-pro-submit-button"
            isPending={isRateProPending}
            isDisabled={!validRating}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
